import React, { useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  VehiclesTypesSelect,
  VehiclesGroupsSelect,
  VehiclesAssetClassesSelect,
} from 'components/AsyncFields';
import * as Yup from 'yup';
import i18n from 'i18n-js';
import { UserContext } from 'context';
import { Formik, Field } from 'formik';
import PropTypes from 'prop-types';
import { ASSET_CLASS } from 'constants/vehicleTypes';
import { FormChanges, parseInputNumber } from 'utils';
import { vehicleActions } from 'state/modules/vehicles';
import { TextField, TextareaField } from 'components/FormFields';
import { Box, Grid, Container, Typography, InputAdornment, styled } from '@mui/material';
import { VehiclesGroupsActions } from 'state/modules/vehiclesGroups';

const StyledContainer = styled(Box)(({ theme }) => ({
  maxWidth: '100%',
  '& input': {
    fontSize: theme.typography.appFontSize,
  },
}));

const VehicleDetails = ({ formRef, errors, step, exit, onSave }) => {
  const cars = ASSET_CLASS;
  const dispatch = useDispatch();

  const { orgType } = useContext(UserContext);
  const { list } = useSelector((state) => state.vehiclesGroups);

  const [showAssetClassSelect, setShowAssetClassSelect] = useState(true);

  const formSchema = Yup.object().shape({
    name: Yup.string().required(i18n.t('common.required_field')),
    vehicle_group_id: Yup.string().required(i18n.t('common.required_field')),
    vehicle_type_id: Yup.string().required(i18n.t('common.required_field')),
    color: Yup.string(),
    price: Yup.number(),
    seats: Yup.number().when('asset_class', {
      is: (val) => val === 'BUS',
      then: Yup.number().min(1),
    }),
    license_plate: Yup.string(),
    asset_class: Yup.string().required(i18n.t('common.required_field')),
    details: Yup.string(),
    vin: Yup.string().when('asset_class', {
      is: (val) => cars.includes(val),
      then: Yup.string().length(17),
    }),
  });

  const vehicleGroupId = orgType === 'plainOrg' && list[0] ? list[0].id : '';

  const initialValues = {
    vin: step?.vin || '',
    name: step?.name || '',
    color: step?.color || '',
    seats: step?.seats || '',
    details: step?.details || '',
    license_plate: step?.license_plate || '',
    vehicle_type_id: step?.vehicle_type?.id || '',
    asset_class: step?.vehicle_type?.asset_class || '',
    price: step?.price ? parseFloat(step?.price / 100).toFixed(2) : '',
    vehicle_group_id:
      step?.vehicle_group?.parent?.id || step?.vehicle_group?.id || vehicleGroupId || '',
  };

  const onSubmit = (values) => {
    let data = { ...values };

    delete data.asset_class;

    // remove empty fields
    Object.keys(data).forEach((key) => !data[key] && delete data[key]);

    let payload = {};

    if (step?.id) {
      payload = { ...data };
    } else {
      payload = {
        ...data,
        group_type: 'OWNER',
      };
    }

    if (step?.vehicle_group?.id === payload.vehicle_group_id) {
      delete payload.vehicle_group_id;
    }

    if (payload.price) {
      payload.price = parseInputNumber(payload.price);
    }

    dispatch(vehicleActions.fetchCreateVehicle(payload, 'details', exit));
  };

  const fetchVehiclesGroups = (params) => {
    const newParams = { ...params };
    dispatch(VehiclesGroupsActions.fetchVehicleGroups(newParams));
  };

  useEffect(() => {
    if (orgType === 'plainOrg') {
      fetchVehiclesGroups({
        page: 1,
        limit: 20,
        group_type: 'OWNER',
      });
    }
  }, [orgType]); // eslint-disable-line

  const disabledField = orgType === 'msServiceOrg' ? true : false;

  let params = {};

  if (step?.vehicle_type?.id && orgType !== 'msServiceOrg') {
    params.id = step?.vehicle_type?.id;
  }

  return (
    <StyledContainer>
      <Container className="h-100">
        <Grid container direction="row" justifyContent="center" alignItems="center">
          <Grid size={{ xs: 11 }}>
            <Formik
              innerRef={formRef}
              initialValues={initialValues}
              validationSchema={formSchema}
              enableReinitialize={true}
              onSubmit={onSubmit}
            >
              {({ handleSubmit, values }) => (
                <form noValidate onSubmit={handleSubmit}>
                  <Grid
                    container
                    spacing={4}
                    direction="row"
                    justifyContent="flex-start"
                    className={disabledField ? 'disabled' : ''}
                    alignItems="flex-start"
                  >
                    <Grid size={{ xs: 12 }}>
                      <Typography variant="h6" gutterBottom>
                        <Box sx={{ fontWeight: 500, fontSize: 14, my: 1 }}>
                          {i18n.t('vehicle.wizard.vehicle_details')}
                        </Box>
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <Field
                        type="text"
                        name="name"
                        disabled={disabledField}
                        component={TextField}
                        asyncErrors={errors?.name}
                        label={i18n.t('vehicle.wizard.name')}
                      />
                    </Grid>
                    {showAssetClassSelect && (
                      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <Field
                          name="asset_class"
                          disabled={disabledField}
                          customParams={{ asset_class: values.asset_class }}
                          component={VehiclesAssetClassesSelect}
                          label={i18n.t('vehicle.wizard.asset_class')}
                          onHide={() => setShowAssetClassSelect(false)}
                        />
                      </Grid>
                    )}
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <Field
                        name="vehicle_type_id"
                        component={VehiclesTypesSelect}
                        label={i18n.t('vehicle.wizard.vehicle_type')}
                        disabled={disabledField || !values?.asset_class}
                        customParams={{ asset_class: values.asset_class, ...params }}
                      />
                    </Grid>
                    <Grid container size={{ xs: 12 }} spacing={4}>
                      <>
                        {cars.includes(values.asset_class) && (
                          <>
                            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                              <Field
                                type="text"
                                name="license_plate"
                                disabled={disabledField}
                                component={TextField}
                                required={false}
                                asyncErrors={errors?.license_plate}
                                label={i18n.t('vehicle.wizard.license_plate')}
                              />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                              <Field
                                name="vin"
                                type="text"
                                required={false}
                                component={TextField}
                                disabled={disabledField}
                                asyncErrors={errors?.vin}
                                label={i18n.t('vehicle.wizard.vin')}
                              />
                            </Grid>
                          </>
                        )}
                        <Grid
                          size={{
                            xs: 12,
                            sm: 6,
                            md: !cars.includes(values.asset_class) ? 6 : 4,
                          }}
                        >
                          <Field
                            step="any"
                            type="number"
                            name="price"
                            format="price"
                            required={false}
                            component={TextField}
                            disabled={disabledField}
                            asyncErrors={errors?.price}
                            disabledText={disabledField}
                            label={i18n.t('vehicle.wizard.price')}
                            startAdornment={
                              <InputAdornment position="start" sx={{ mr: '-8px' }}>
                                $
                              </InputAdornment>
                            }
                          />
                        </Grid>
                      </>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: !cars.includes(values.asset_class) ? 6 : 4 }}>
                      <Field
                        type="text"
                        name="color"
                        required={false}
                        component={TextField}
                        disabled={disabledField}
                        asyncErrors={errors?.color}
                        label={i18n.t('vehicle.wizard.color')}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 8 }}>
                      <Field
                        rows={2}
                        type="text"
                        name="details"
                        required={false}
                        variant="standard"
                        disabled={disabledField}
                        component={TextareaField}
                        asyncErrors={errors?.details}
                        label={i18n.t('vehicle.wizard.description')}
                      />
                    </Grid>
                    {values.asset_class === 'BUS' && (
                      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <Field
                          name="seats"
                          type="number"
                          component={TextField}
                          disabled={disabledField}
                          asyncErrors={errors?.seats}
                          label={i18n.t('vehicle.wizard.seats')}
                        />
                      </Grid>
                    )}
                    {orgType === 'msOwnerOrg' && (
                      <>
                        <Grid
                          size={{ xs: 12 }}
                          sx={{
                            pb: 0,
                          }}
                        >
                          <Typography variant="h6" gutterBottom>
                            <Box sx={{ fontWeight: 500, fontSize: 14, my: 1 }}>
                              {i18n.t('vehicle.wizard.fleet')}
                            </Box>
                          </Typography>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                          <Field
                            name="vehicle_group_id"
                            component={VehiclesGroupsSelect}
                            showModal={step?.id ? true : false}
                            label={i18n.t('vehicle.wizard.fleet')}
                            customParams={{ group_type: 'OWNER' }}
                          />
                        </Grid>
                      </>
                    )}
                  </Grid>
                  <FormChanges initialValues={initialValues} onSave={onSave} />
                </form>
              )}
            </Formik>
          </Grid>
        </Grid>
      </Container>
    </StyledContainer>
  );
};

VehicleDetails.propTypes = {
  formRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.any })]),
  errors: PropTypes.object,
  step: PropTypes.object,
  exit: PropTypes.func,
  onSave: PropTypes.func,
};

VehicleDetails.defaultProps = {
  formRef: () => {},
  errors: {},
  step: {},
  exit: () => {},
  onSave: () => {},
};

export default VehicleDetails;
